/**
 * Detecta conquistas recém-desbloqueadas e as enfileira pro banner.
 *
 * Não há push do back — o evaluator concede de forma assíncrona. Então
 * fazemos polling de `useMyBadges` (refetchInterval no service) e, a cada
 * resposta, comparamos com o seen-set local:
 *
 *  - 1ª resposta da sessão (seeding): absorve tudo em silêncio, não
 *    notifica — senão o usuário veria suas badges antigas como "novas".
 *  - respostas seguintes: o que for novo + recente + não-backfill vira banner.
 *
 * O seen-set é por usuário (localStorage) — troca de conta re-seeda.
 */

import { useEffect, useRef } from 'react'
import { useAuth } from './use-auth'
import { useTrophyQueue } from './use-trophy-queue'
import { useMyBadges } from '@/services/badges.service'
import { computeNextSeen, diffNewTrophies } from '@/lib/trophy-diff'
import { readSeenSet, writeSeenSet } from '@/lib/trophy-storage'

export function useBadgeUnlockDetector(): void {
  const { user } = useAuth()
  // Só faz polling com sessão ativa — em página pública um 401 em
  // `/users/me/badges` redirecionaria o visitante pra /auth.
  const { data } = useMyBadges({ enabled: !!user })
  const { enqueue } = useTrophyQueue()

  // Usuário pra quem já fizemos o seeding silencioso nesta montagem.
  const seededUserRef = useRef<string | null>(null)

  useEffect(() => {
    const userId = user?.id
    if (!userId || !data) return

    const seen = readSeenSet(userId)

    // Primeira resposta pra este usuário: absorve tudo, não notifica.
    if (seededUserRef.current !== userId) {
      seededUserRef.current = userId
      writeSeenSet(userId, computeNextSeen(data, seen))
      return
    }

    const fresh = diffNewTrophies(data, seen, Date.now())
    if (fresh.length > 0) enqueue(fresh)
    writeSeenSet(userId, computeNextSeen(data, seen))
  }, [data, user?.id, enqueue])
}
